/**
 * Actor — a visible, stateful map entity with a sprite.
 *
 * Used for player bases and enemy bases. Each Actor sits on a single tile,
 * has HP, optional auto-turret, damage VFX, and fog-of-war visibility.
 * Implements IBaseTarget so projectiles can target it directly.
 */

import Phaser from 'phaser';
import { TILE_SIZE, ActorType } from '@/config/GameConfig';
import { GOD } from '@/config/GodMode';
import { IBaseTarget } from '@/entities/GameBase';
import { EventBus, GameEvents } from '@/events/EventBus';
import { SoundManager } from '@/systems/SoundManager';

// ── Turret config ──
export const BASE_TURRET_RANGE = TILE_SIZE * 3.5;
const BASE_TURRET_DAMAGE = 3;
const BASE_TURRET_COOLDOWN = 1200; // ms
const BASE_TURRET_PROJ_SPEED = 180; // px/s

// ── Actor options ──
export interface ActorOptions {
  hp: number;
  textureKey: string;
  hasTurret?: boolean;
  id?: string;
  pathIndex?: number;
  /** Display name shown above the sprite (e.g. "Alpha", "Beta") */
  label?: string;
}

// ── Enemy target (minimal interface for turret targeting) ──
export interface TurretTarget {
  alive: boolean;
  body: { x: number; y: number };
  takeDamage(amount: number): void;
}

/** Upgrade-derived params passed from PlayerBase to Actor turret */
export interface TurretUpgradeParams {
  bonusRangeTiles: number;  // extra range in tiles
  bonusDamage: number;      // extra damage per hit
  totalCannons: number;     // total cannon count (base 1 + upgrades)
}

export class Actor implements IBaseTarget {
  // ── Position ──
  public readonly x: number;
  public readonly y: number;
  public readonly col: number;
  public readonly row: number;
  public readonly actorType: ActorType;
  public readonly collisionRadius: number;
  public readonly id: string;
  public readonly pathIndex: number;
  /** Display name (e.g. "Alpha", "Beta") — shown above sprite for player bases */
  public readonly label: string;

  // ── State ──
  public hp: number;
  public maxHp: number;
  public alive = true;

  // ── Visuals ──
  private scene: Phaser.Scene;
  private sprite: Phaser.GameObjects.Image;
  private hpBarBg: Phaser.GameObjects.Rectangle;
  private hpBar: Phaser.GameObjects.Rectangle;
  private hpText: Phaser.GameObjects.Text;

  // ── Label ──
  private labelText: Phaser.GameObjects.Text | null = null;

  // ── Damage VFX ──
  private smokeEmitters: Phaser.GameObjects.Image[] = [];
  private flameEmitters: Phaser.GameObjects.GameObject[] = [];
  private damageVfxTimer: Phaser.Time.TimerEvent | null = null;
  private lastSeverity = 0;

  // ── Turret ──
  private hasTurret: boolean;
  private turretCooldown = 0;
  private turretProjectiles: ActorTurretProjectile[] = [];

  constructor(scene: Phaser.Scene, col: number, row: number, actorType: ActorType, options: ActorOptions) {
    this.scene = scene;
    this.col = col;
    this.row = row;
    this.actorType = actorType;
    this.x = col * TILE_SIZE + TILE_SIZE / 2;
    this.y = row * TILE_SIZE + TILE_SIZE / 2;
    this.collisionRadius = TILE_SIZE / 2;
    this.hp = options.hp;
    this.maxHp = options.hp;
    this.id = options.id ?? `${actorType}_${col}_${row}`;
    this.pathIndex = options.pathIndex ?? 0;
    this.hasTurret = options.hasTurret ?? false;
    this.label = options.label ?? '';

    // ── Sprite ──
    this.sprite = scene.add.image(this.x, this.y, options.textureKey)
      .setDisplaySize(TILE_SIZE, TILE_SIZE)
      .setDepth(2.0);

    // ── Name label (player bases with multiple nodes) ──
    if (this.label && actorType === ActorType.PLAYER_BASE) {
      this.labelText = scene.add.text(this.x, this.y + TILE_SIZE / 2 + 4, this.label, {
        fontSize: '7px',
        color: '#44cccc',
        fontFamily: 'monospace',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 2,
      }).setOrigin(0.5, 0).setDepth(32);
    }

    // ── HP bar ──
    const barW = actorType === ActorType.ENEMY_BASE ? 28 : 24;
    const barH = 3;
    const barY = this.y - TILE_SIZE / 2 - 6;
    const barColor = actorType === ActorType.ENEMY_BASE ? 0xff4444 : 0x44ff44;

    this.hpBarBg = scene.add.rectangle(this.x, barY, barW, barH, 0x333333).setDepth(31);
    this.hpBar = scene.add.rectangle(this.x, barY, barW, barH, barColor).setDepth(32);
    this.hpText = scene.add.text(this.x, barY - 6, `${this.hp}`, {
      fontSize: '7px',
      color: actorType === ActorType.ENEMY_BASE ? '#ffaaaa' : '#ffffff',
      fontFamily: 'monospace',
    }).setOrigin(0.5).setDepth(32);

    // Player base nodes: hide HP text by default (shown on hover via UIScene)
    if (actorType === ActorType.PLAYER_BASE) {
      this.hpText.setVisible(false);
    }
  }

  // ── IBaseTarget stubs ──
  stun(): void { /* actors can't be stunned */ }
  knockBack(): void { /* actors can't be knocked back */ }
  applyBurn(): void { /* actors can't burn */ }
  getVelocity(): { vx: number; vy: number } { return { vx: 0, vy: 0 }; }

  // ── Damage ──

  /** Apply damage (already scaled by caller). Returns true if this killed the actor. */
  applyDamage(dmg: number): boolean {
    if (!this.alive) return false;
    this.hp -= dmg;
    if (this.hp < 0) this.hp = 0;
    this.updateVisuals();
    this.updateDamageVfx();
    this.spawnHitFlash();
    if (this.hp <= 0) {
      this.alive = false;
      this.onDestroyed();
      return true;
    }
    return false;
  }

  /** IBaseTarget.takeDamage — for direct projectile hits on enemy bases */
  takeDamage(amount: number): void {
    if (!this.alive) return;
    this.applyDamage(amount);
    if (this.actorType === ActorType.ENEMY_BASE) {
      EventBus.emit(GameEvents.ENEMY_BASE_DAMAGED, {
        baseId: this.id, hp: this.hp, maxHp: this.maxHp,
      });
    }
  }

  // ── Heal / Upgrade ──

  /** Heal HP (from repair robots) */
  heal(amount: number): void {
    if (!this.alive) return;
    this.hp = Math.min(this.maxHp, this.hp + amount);
    this.updateVisuals();
    this.updateDamageVfx();
  }

  /** Add to max HP and current HP (from base upgrade) */
  addHp(amount: number): void {
    this.maxHp += amount;
    this.hp += amount;
    this.updateVisuals();
    this.updateDamageVfx();
  }

  // ── Turret ──

  /** Update auto-turret. Call each frame during WAVE phase.
   *  @param upgradeParams — upgrade-derived stats from PlayerBase (optional for enemy bases) */
  updateTurret(dt: number, enemies: TurretTarget[], upgradeParams?: TurretUpgradeParams): void {
    if (!this.hasTurret || !this.alive) return;
    const bonusRange = (upgradeParams?.bonusRangeTiles ?? 0) * TILE_SIZE;
    const effectiveRange = (BASE_TURRET_RANGE + bonusRange) * GOD.baseTurretRangeMult;
    const rangeSq = effectiveRange * effectiveRange;
    const bonusDmg = upgradeParams?.bonusDamage ?? 0;
    const totalCannons = upgradeParams?.totalCannons ?? 1;

    this.turretCooldown -= dt * 1000;
    if (this.turretCooldown <= 0) {
      // Find up to totalCannons nearest alive enemies in range (each cannon targets a different enemy)
      const targets: TurretTarget[] = [];
      const candidates = enemies.filter(e => {
        if (!e.alive) return false;
        const dx = e.body.x - this.x;
        const dy = e.body.y - this.y;
        return dx * dx + dy * dy < rangeSq;
      });
      // Sort by distance
      candidates.sort((a, b) => {
        const da = (a.body.x - this.x) ** 2 + (a.body.y - this.y) ** 2;
        const db = (b.body.x - this.x) ** 2 + (b.body.y - this.y) ** 2;
        return da - db;
      });
      // Pick up to totalCannons targets (cycle if fewer enemies than cannons)
      for (let c = 0; c < totalCannons && candidates.length > 0; c++) {
        targets.push(candidates[c % candidates.length]);
      }
      if (targets.length > 0) {
        this.turretCooldown = BASE_TURRET_COOLDOWN;
        for (const target of targets) {
          // Slight angle offset for multi-cannon visual spread
          const offsetAngle = targets.length > 1
            ? (targets.indexOf(target) - (targets.length - 1) / 2) * 0.15
            : 0;
          this.turretProjectiles.push(
            new ActorTurretProjectile(this.scene, this.x, this.y, target, bonusDmg, offsetAngle),
          );
        }
      }
    }

    // Update projectiles
    for (let i = this.turretProjectiles.length - 1; i >= 0; i--) {
      const p = this.turretProjectiles[i];
      p.update(dt);
      if (!p.alive) {
        p.destroy();
        this.turretProjectiles.splice(i, 1);
      }
    }
  }

  // ── Fog of War (enemy bases) ──

  /** Set visibility factor for fog of war (0 = hidden, 1 = fully visible) */
  setFogVisibility(factor: number): void {
    const visible = factor > 0.01;
    this.sprite.setVisible(visible);
    if (visible) this.sprite.setAlpha(factor);
    this.hpBarBg.setVisible(visible);
    if (visible) this.hpBarBg.setAlpha(factor);
    this.hpBar.setVisible(visible);
    if (visible) this.hpBar.setAlpha(factor);
    this.hpText.setVisible(visible);
    if (visible) this.hpText.setAlpha(factor);
  }

  // ── Visuals ──

  updateVisuals(): void {
    const ratio = this.maxHp > 0 ? Math.max(0, this.hp / this.maxHp) : 0;
    const barW = this.actorType === ActorType.ENEMY_BASE ? 28 : 24;
    const fillW = barW * ratio;
    this.hpBar.setPosition(this.x - barW / 2 + fillW / 2, this.hpBar.y);
    this.hpBar.setSize(fillW, this.hpBar.height);
    if (this.actorType === ActorType.PLAYER_BASE) {
      this.hpBar.setFillStyle(ratio > 0.5 ? 0x44ff44 : ratio > 0.25 ? 0xffaa00 : 0xff4444);
    }
    this.hpText.setText(`${Math.ceil(this.hp)}`);
  }

  private spawnHitFlash(): void {
    const flash = this.scene.add.rectangle(this.x, this.y, TILE_SIZE, TILE_SIZE, 0xff0000, 0.4).setDepth(30);
    this.scene.tweens.add({ targets: flash, alpha: 0, duration: 200, onComplete: () => flash.destroy() });
  }

  private onDestroyed(): void {
    // Explosion VFX
    if (this.scene.textures.exists('fx_explosion')) {
      const exp = this.scene.add.image(this.x, this.y, 'fx_explosion')
        .setDepth(50).setScale(2).setAlpha(0.9);
      this.scene.tweens.add({
        targets: exp, alpha: 0, scaleX: 3, scaleY: 3,
        duration: 600, onComplete: () => exp.destroy(),
      });
    }
    SoundManager.play('robotExplode');

    if (this.actorType === ActorType.ENEMY_BASE) {
      EventBus.emit(GameEvents.ENEMY_BASE_DESTROYED, {
        baseId: this.id, position: { x: this.x, y: this.y },
      });
      // Fade out sprite
      this.scene.tweens.add({
        targets: this.sprite, alpha: 0, duration: 400,
      });
    }

    // Hide HP bar + label
    this.hpBarBg.setVisible(false);
    this.hpBar.setVisible(false);
    this.hpText.setVisible(false);
    if (this.labelText) this.labelText.setVisible(false);
  }

  // ── Damage VFX (smoke/flames) ──

  private updateDamageVfx(): void {
    const ratio = this.maxHp > 0 ? Math.max(0, this.hp / this.maxHp) : 0;
    const severity = 1 - ratio;
    if (severity < 0.3) { this.clearDamageVfx(); this.lastSeverity = severity; return; }
    this.lastSeverity = severity;
    if (!this.damageVfxTimer) {
      this.damageVfxTimer = this.scene.time.addEvent({
        delay: 180, loop: true, callback: () => this.emitDamageParticles(),
      });
    }
  }

  private emitDamageParticles(): void {
    if (!this.alive && this.lastSeverity < 1) { this.clearDamageVfx(); return; }
    const severity = this.lastSeverity;
    const halfTile = TILE_SIZE / 2;
    const smokeCount = severity < 0.5 ? 2 : severity < 0.75 ? 3 : 5;
    for (let i = 0; i < smokeCount; i++) {
      const ox = (Math.random() - 0.5) * halfTile * 1.2;
      const oy = (Math.random() - 0.5) * halfTile * 0.6;
      const grey = Math.round(255 * (1 - severity * 0.85));
      const tint = (grey << 16) | (grey << 8) | grey;
      if (this.scene.textures.exists('fx_smoke')) {
        const smoke = this.scene.add.image(this.x + ox, this.y + oy, 'fx_smoke')
          .setDepth(35).setScale(0.6 + severity * 1.0).setAlpha(0.6 + severity * 0.35).setTint(tint);
        this.smokeEmitters.push(smoke);
        this.scene.tweens.add({
          targets: smoke, y: smoke.y - 15 - Math.random() * 20, alpha: 0,
          scaleX: smoke.scaleX * 1.8, scaleY: smoke.scaleY * 1.8,
          duration: 800 + Math.random() * 600,
          onComplete: () => { smoke.destroy(); const idx = this.smokeEmitters.indexOf(smoke); if (idx >= 0) this.smokeEmitters.splice(idx, 1); },
        });
      }
    }
    if (severity > 0.4) {
      const flameCount = severity < 0.6 ? 1 : severity < 0.8 ? 2 : Math.ceil(Math.random() * 2) + 2;
      for (let i = 0; i < flameCount; i++) {
        const fx = this.x + (Math.random() - 0.5) * halfTile;
        const fy = this.y + (Math.random() - 0.5) * halfTile * 0.8;
        const flameSize = 3 + severity * 4;
        const flameColor = Math.random() < 0.4 ? 0xffdd44 : 0xff6600;
        const flame = this.scene.add.rectangle(fx, fy, flameSize, flameSize * 1.5, flameColor, 0.7).setDepth(36);
        this.flameEmitters.push(flame);
        this.scene.tweens.add({
          targets: flame, y: fy - 6 - Math.random() * 8, scaleX: 0.3, scaleY: 1.5 + Math.random() * 0.5, alpha: 0,
          duration: 300 + Math.random() * 400,
          onComplete: () => { flame.destroy(); const idx = this.flameEmitters.indexOf(flame); if (idx >= 0) this.flameEmitters.splice(idx, 1); },
        });
      }
    }
  }

  private clearDamageVfx(): void {
    if (this.damageVfxTimer) { this.damageVfxTimer.destroy(); this.damageVfxTimer = null; }
    for (const s of this.smokeEmitters) s.destroy();
    this.smokeEmitters = [];
    for (const f of this.flameEmitters) f.destroy();
    this.flameEmitters = [];
  }

  // ── Cleanup ──

  destroy(): void {
    this.clearDamageVfx();
    for (const p of this.turretProjectiles) p.destroy();
    this.turretProjectiles = [];
    this.sprite.destroy();
    this.hpBarBg.destroy();
    this.hpBar.destroy();
    this.hpText.destroy();
    if (this.labelText) { this.labelText.destroy(); this.labelText = null; }
  }
}

// ── Actor turret projectile (small homing bullet) ──

class ActorTurretProjectile {
  alive = true;
  private sprite: Phaser.GameObjects.Rectangle;
  private px: number;
  private py: number;
  private vx: number;
  private vy: number;
  private target: TurretTarget;
  private bonusDamage: number;
  private maxLife = 2000;
  private static readonly HIT_RADIUS_SQ = 16 * 16; // 16px hit radius

  constructor(scene: Phaser.Scene, x: number, y: number, target: TurretTarget, bonusDamage = 0, angleOffset = 0) {
    this.px = x;
    this.py = y;
    this.target = target;
    this.bonusDamage = bonusDamage;
    // Straight line toward target's current position (no homing)
    const dx = target.body.x - x;
    const dy = target.body.y - y;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    // Apply angle offset for multi-cannon spread
    const baseAngle = Math.atan2(dy, dx) + angleOffset;
    this.vx = Math.cos(baseAngle) * BASE_TURRET_PROJ_SPEED;
    this.vy = Math.sin(baseAngle) * BASE_TURRET_PROJ_SPEED;
    this.sprite = scene.add.rectangle(x, y, 3, 3, 0x44ff88, 0.9).setDepth(25);
  }

  update(dt: number): void {
    if (!this.alive) return;
    this.maxLife -= dt * 1000;
    if (this.maxLife <= 0) { this.alive = false; return; }

    // Target died — kill projectile immediately
    if (!this.target.alive) { this.alive = false; return; }

    this.px += this.vx * dt;
    this.py += this.vy * dt;
    this.sprite.setPosition(this.px, this.py);

    // Hit check (16px radius)
    const dx = this.target.body.x - this.px;
    const dy = this.target.body.y - this.py;
    if (dx * dx + dy * dy < ActorTurretProjectile.HIT_RADIUS_SQ) {
      const totalDmg = (BASE_TURRET_DAMAGE + this.bonusDamage) * GOD.baseTurretDamageMult;
      this.target.takeDamage(totalDmg);
      this.alive = false;
    }
  }

  destroy(): void {
    this.sprite.destroy();
  }
}
